import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import { useLocale } from "@/context/LocaleContext";
import { AuthPageHeader } from "@/components/AuthPageHeader";
import { translate } from "@/lib/uiStrings";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function formatForgotPasswordError(err, lang) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  const st = err?.response?.status;
  if (!err?.response && err?.message === "Network Error") {
    return translate(lang, "auth.errNetwork");
  }
  if (st) return translate(lang, "auth.errServer", { status: String(st) });
  if (err?.message) return err.message;
  return translate(lang, "auth.errGeneric");
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const { forgotPassword } = useAuth();
  const { lang, t } = useLocale();

  const fieldClass =
    "mt-1 h-10 rounded-lg border-[var(--line-2)] bg-[var(--bg)] shadow-none focus-visible:ring-1 focus-visible:ring-[var(--line)] focus-visible:border-[var(--line)]";

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await forgotPassword(email);
      setSent(true);
      toast.success(t("forgotPassword.sentToast"));
    } catch (err) {
      toast.error(formatForgotPasswordError(err, lang));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain flex flex-col bg-[var(--bg)] overflow-x-hidden">
      <AuthPageHeader />
      <div className="flex flex-1 flex-col items-center justify-center px-4 pb-8">
        <div className="w-full max-w-[min(400px,100%)] rounded-2xl border border-[var(--line-2)] bg-[var(--panel-2)] shadow-sm p-5 sm:p-6">
          <header className="mb-4">
            <p className="text-sm font-medium text-[var(--text)] leading-snug">
              {t("forgotPassword.title")}
            </p>
            <p className="mt-1 text-[var(--text-dim)] text-sm leading-snug">
              {t("forgotPassword.subtitle")}
            </p>
          </header>

          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <Label className="text-xs font-medium text-[var(--text-dim)]">
                {t("forgotPassword.emailLabel")}
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={fieldClass}
              />
            </div>

            {sent && (
              <p className="rounded-lg border border-[var(--accent)]/40 bg-[var(--accent)]/10 px-3 py-2 text-sm leading-snug text-[var(--accent)]">
                {t("forgotPassword.sentMessage")}
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full h-10 rounded-lg bg-[var(--text)] text-[var(--bg)] text-sm font-semibold tracking-wide hover:opacity-90 transition-opacity disabled:opacity-45 disabled:pointer-events-none"
            >
              {busy ? t("forgotPassword.submitBusy") : t("forgotPassword.submit")}
            </button>
          </form>
        </div>
        <Link
          to="/login"
          className="mt-5 text-sm text-[var(--text-dim)] transition-colors hover:text-[var(--text)]"
        >
          {t("forgotPassword.backToLogin")}
        </Link>
      </div>
    </div>
  );
}
